import API from "../../../../core/api";
import { getGuestAuthSettings } from "../../../../core/utils";
import { isEmailValid } from "../../../../shared/validations/emailValidations";
import OnlineInvitationExclusions from "./OnlineInvitationExclusions";
import { css } from "@emotion/react";
import {
  Alert,
  Button,
  Icon,
  Logo,
  styleUtils,
  TextInput,
  TextLink,
  theme,
} from "@minted/minted-components";
import React, { memo, useCallback, useState } from "react";

const onlineInvitationSignupStyles = {
  exclusionsLinkStyles: css`
    margin-top: ${theme.spacing.x2};
    display: flex;
    justify-content: center;
  `,
  formStyles: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${theme.spacing.x4};
    max-width: ${styleUtils.rem(400)};
    width: 100%;
  `,
  headlineStyles: css`
    ${theme.typeStyles.displayExtraSmallStyles};

    margin-left: auto;
    margin-right: auto;
    text-align: center;
  `,
  inputFieldStyles: css`
    width: ${styleUtils.rem(225)};
  `,
  logoWrapperStyles: css`
    margin-left: auto;
    margin-right: auto;
    width: ${styleUtils.rem(60)};
  `,
  signupWrapperStyles: css`
    background-color: ${theme.colors.notice200};
    display: flex;
    flex-direction: column;
    padding-bottom: ${theme.spacing.x5};
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    padding-top: ${theme.spacing.x5};
  `,
  submitButtonStyles: css`
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    flex-shrink: 0;
  `,
  successIconStyles: css`
    align-items: center;
    color: ${theme.colors.positive600};
    display: flex;
    flex-shrink: 0;
    width: ${styleUtils.rem(24)};
  `,
  successMessageStyles: css`
    ${theme.typeStyles.bodySmallStyles};
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing.base};
    justify-content: center;
    margin-top: ${theme.spacing.x4};
  `,
};

const cypressScope = "signupCallout";

type Props = {
  defaultEmail?: string;
};

const OnlineInvitationSignup = ({ defaultEmail = "" }: Props) => {
  const [email, setEmail] = useState(defaultEmail);
  const [isExclusionsModalOpen, setIsExclusionsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);

  const onEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      setShowEmailError(false);
      setHasSubmitError(false);
    },
    [],
  );

  const onSubmit = useCallback(() => {
    if (!isEmailValid(email)) {
      setShowEmailError(true);

      return;
    }

    setIsSubmitting(true);
    setHasSubmitError(false);

    API.post("guest-management/send-oi-promo-email", getGuestAuthSettings(), {
      email,
    })
      .then(() => {
        setHasSubmitted(true);
      })
      .catch(() => {
        setHasSubmitError(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [email]);

  return (
    <div
      css={onlineInvitationSignupStyles.signupWrapperStyles}
      data-cy={cypressScope}
    >
      <div css={onlineInvitationSignupStyles.logoWrapperStyles}>
        <Logo modifier="minted" />
      </div>
      <div
        css={onlineInvitationSignupStyles.headlineStyles}
        data-cy={`${cypressScope}Headline`}
      >
        20% off your first order
      </div>
      <div css={onlineInvitationSignupStyles.exclusionsLinkStyles}>
        <TextLink
          data-cy={`${cypressScope}Exclusions`}
          onClick={() => setIsExclusionsModalOpen(true)}
          size="small"
          text="Exclusions apply."
          type={TextLink.textLinkTypes.tertiary}
        />
      </div>
      {hasSubmitted ? (
        <div
          css={onlineInvitationSignupStyles.successMessageStyles}
          data-cy={`${cypressScope}Success`}
        >
          <div css={onlineInvitationSignupStyles.successIconStyles}>
            <Icon type={Icon.types.check} />
          </div>
          Your promo code has been sent.
        </div>
      ) : (
        <>
          <div css={onlineInvitationSignupStyles.formStyles}>
            <div css={onlineInvitationSignupStyles.inputFieldStyles}>
              <TextInput
                data-cy={`${cypressScope}Email`}
                onChange={onEmailChange}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    onSubmit();
                  }
                }}
                placeholder="Email address"
                size={TextInput.textInputSizes.medium}
                touched
                value={email}
              />
            </div>
            <div css={onlineInvitationSignupStyles.submitButtonStyles}>
              <Button
                data-cy={`${cypressScope}Submit`}
                disabled={isSubmitting}
                onClick={onSubmit}
                size={Button.sizes.large}
                text="SUBMIT"
                type={Button.types.primary}
              />
            </div>
          </div>
          <Alert
            primaryText="Please enter a valid email address."
            show={showEmailError}
            type="negative"
          />
          <Alert
            primaryText="Something went wrong. Please try again."
            show={hasSubmitError}
            type="negative"
          />
        </>
      )}
      <OnlineInvitationExclusions
        close={() => setIsExclusionsModalOpen(false)}
        isOpen={isExclusionsModalOpen}
      />
    </div>
  );
};

export default memo(OnlineInvitationSignup);
